// Vendor imports
const classNames = require('classnames')
const PropTypes = require('prop-types')
const React = require('react')
// Shared imports
const BemEntity = require('shared/bem')
const {
  TYPES: modelTypes,
  TYPES_KEYS: modelTypesKeys
} = require('shared/model-types')
// Client V2 common imports
const tag = require('client-v2/common/utils/hyperscript')
const Modal = require('client-v2/common/components/modal')
const InputText = require('client-v2/common/components/input-text')
const ArticleModelRecord = require('client-v2/common/models/article/record')
const CollectionModelRecord = require('client-v2/common/models/collection/record')
const UserModelRecord = require('client-v2/common/models/user/record')
// Sibling imports
const ShareModalArticlePreview = require('./components/article-preview')
const ShareModalMinimalContent = require('./components/minimal-content')
const ShareModalShareFriends = require('./components/share-friends-smart')
const ShareModalShareButton = require('./components/share-button')
const { socialShareHref } = require('./utils')


const bem = BemEntity('ShareModal')

class ShareModal extends React.PureComponent {
  constructor(props) {
    super(props)

    this.state = {
      copied: false
    }

    // Refs
    this.copyInputRef = React.createRef()

    // Bind event listeners
    this.onCopy = this.onCopy.bind(this)
    this.onShare = this.onShare.bind(this)
  }

  static getDerivedStateFromProps(props) {
    if (!props.model) {
      return { copied: false }
    }
    return null
  }

  // Event handler methods

  onCopy(event) {
    event.preventDefault()
    this.props.onCopy({
      input: this.copyInputRef.current,
      model: this.props.model
    })
    this.setState(() => ({ copied: true }))
  }

  onShare(event) {
    const { model } = this.props
    const anchor = event.currentTarget
    const href = anchor.getAttribute('href')
    const { shareType } = anchor.dataset
    if (shareType !== 'email') {
      /**
       * `mailto:` links need to be handled by the default `<a href>` click behavior
       * @see https://app.asana.com/0/226524042134185/1137183849467687/f
       */
      event.preventDefault()
    }
    this.props.onShare({ href, model, shareType })
  }

  // Component render methods

  renderArticleContent() {
    const { props } = this
    return (
      tag('div', {
        className: bem.element('inner')
      },
        tag('div', {
          className: bem.element('leading')
        },
          tag(ShareModalArticlePreview, {
            article: props.model,
            ...props
          }),
          this.renderSocialButtons()
        ),
        tag(ShareModalShareFriends, {
          className: bem.element('trailing'),
          ...props
        })
      )
    )
  }

  renderMinimalContent() {
    const { model } = this.props
    return ([
      tag('div', {
        className: bem.element('header')
      },
        'Share'
      ),
      tag(ShareModalMinimalContent, {
        model
      }),
      this.renderSocialButtons()
    ])
  }


  renderContent() {
    const { model } = this.props
    switch (model.type) {
      case modelTypes.ARTICLE:
        return this.renderArticleContent()
      default:
        return this.renderMinimalContent()
    }
  }

  renderSocialButtons() {
    const { model, socialMediums } = this.props
    const { copied } = this.state
    return ([
      copied && tag('div', {
        className: bem.element('copied'),
        key: 'copied'
      },
        'Copied!'
      ),
      tag('div', {
        className: bem.element('hrefs-container'),
        key: 'mediums'
      },
        socialMediums.map(medium => (
          tag(ShareModalShareButton, {
            key: medium.id,
            // Values
            className: bem.element('href'),
            href: medium.id === 'copy' ? '' : socialShareHref(model, medium.id),
            name: medium.name,
            shareType: medium.id,
            // Callbacks
            onClick: medium.id === 'copy' ? this.onCopy : this.onShare
          })
        )),
        tag(InputText, {
          inputRef: this.copyInputRef,
          className: bem.element('copy-input'),
          readOnly: true,
          value: socialShareHref(model, 'copy')
        })
      )
    ])
  }

  // Root render method

  render() {
    const { props } = this
    if (!props.model) {
      return null
    }
    return (
      tag(Modal, {
        ariaLabel: `Share ${props.model.type}`,
        className: classNames(
          props.className,
          bem.block({
            type: modelTypesKeys[props.model.type]
          })
        ),
        onDismiss: props.onDismiss
      },
        this.renderContent()
      )
    )
  }
}


ShareModal.propTypes = {
  // Values
  className: PropTypes.string,
  model: PropTypes.oneOfType([
    PropTypes.shape(ArticleModelRecord.propTypes),
    PropTypes.shape(CollectionModelRecord.propTypes),
    PropTypes.shape(UserModelRecord.propTypes)
  ]),
  socialMediums: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    windowSize: PropTypes.array
  })).isRequired,
  // Callbacks
  onCopy: PropTypes.func.isRequired,
  onDismiss: PropTypes.func.isRequired,
  onShare: PropTypes.func.isRequired
}

module.exports = ShareModal
